import { jwtDecode } from 'jwt-decode';
import api from './api';
import { ENDPOINTS, STORAGE_KEYS } from '../utils/constants';
import { formatApiError, logError } from '../utils/errorHandler';

/**
 * Authentication service functions for user signup, login, logout, and token management
 *
 * SECURITY NOTE: This implementation uses localStorage for token storage.
 * Storing tokens in localStorage exposes them to XSS attacks. In production:
 * - Implement Content Security Policy (CSP) headers
 * - Use httpOnly cookies (requires backend changes) for sensitive tokens
 * - Regularly audit dependencies for XSS vulnerabilities
 * - Implement token rotation and short expiration times
 */

/**
 * Store authentication data in localStorage
 * @param {Object} authData - Authentication response data
 * @param {string} authData.access - Access token
 * @param {string} authData.refresh - Refresh token
 * @param {Object} authData.user - User object
 * @param {Object} authData.profile - Profile object
 */
const storeAuthData = ({ access, refresh, user, profile }) => {
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access);
  localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh);
  localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  localStorage.setItem(STORAGE_KEYS.PROFILE, JSON.stringify(profile));
};

/**
 * Clear all authentication data from localStorage
 */
export const clearAuthData = () => {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER);
  localStorage.removeItem(STORAGE_KEYS.PROFILE);
};

/**
 * Check if a JWT token is expired
 * @param {string} token - JWT token
 * @returns {boolean} - True if token is expired
 */
const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch (error) {
    logError('Token validation', error);
    return true;
  }
};

/**
 * Sign up a new customer account
 * @param {Object} userData - User registration data
 * @param {string} userData.email - User email
 * @param {string} userData.password - User password
 * @param {string} userData.first_name - User first name
 * @param {string} userData.last_name - User last name
 * @param {string} userData.company_name - Company name
 * @param {string} userData.phone - Company phone number
 * @returns {Promise<Object>} - Response with user data and tokens
 * @throws {Object} - Formatted error object
 */
export const signup = async (userData) => {
  try {
    const response = await api.post(ENDPOINTS.AUTH.SIGNUP, userData);
    // Signup now returns a pending message — no tokens to store
    return response.data;
  } catch (error) {
    logError('Signup', error);
    throw formatApiError(error);
  }
};

/**
 * Login an existing user
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} - Response with user data and tokens
 * @throws {Object} - Formatted error object
 */
export const login = async (email, password) => {
  try {
    const response = await api.post(ENDPOINTS.AUTH.LOGIN, { email, password });
    const { access, refresh, user, profile } = response.data;

    storeAuthData({ access, refresh, user, profile });

    return response.data;
  } catch (error) {
    logError('Login', error);
    throw formatApiError(error);
  }
};

/**
 * Logout the current user
 * @returns {Promise<void>}
 */
export const logout = async () => {
  try {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

    if (refreshToken) {
      // Call backend logout endpoint to blacklist refresh token
      await api.post(ENDPOINTS.AUTH.LOGOUT, { refresh: refreshToken });
    }
  } catch (error) {
    // Continue with local logout even if backend call fails
    logError('Logout', error);
  } finally {
    // Always clear local storage
    clearAuthData();
  }
};

/**
 * Get the current authenticated user from localStorage
 * @returns {Object|null} - User object or null if not authenticated
 */
export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem(STORAGE_KEYS.USER);
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    logError('Get current user', error);
    return null;
  }
};

/**
 * Get the current user's profile from localStorage
 * @returns {Object|null} - Profile object or null if not authenticated
 */
export const getCurrentProfile = () => {
  try {
    const profileStr = localStorage.getItem(STORAGE_KEYS.PROFILE);
    return profileStr ? JSON.parse(profileStr) : null;
  } catch (error) {
    logError('Get current profile', error);
    return null;
  }
};

/**
 * Check if user is authenticated with valid token
 * @returns {boolean} - True if user has valid, non-expired access token
 */
export const isAuthenticated = () => {
  const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  if (!accessToken) return false;

  // Check if token is expired
  return !isTokenExpired(accessToken);
};

/**
 * Get access token from localStorage
 * @returns {string|null} - Access token or null
 */
export const getAccessToken = () => {
  return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
};

/**
 * Get refresh token from localStorage
 * @returns {string|null} - Refresh token or null
 */
export const getRefreshToken = () => {
  return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
};

/**
 * Impersonate a user (superuser only)
 * @param {number} userId - Target user's ID
 * @returns {Promise<void>}
 * @throws {Object} - Formatted error object
 */
export const impersonateUser = async (userId) => {
  try {
    const response = await api.post(ENDPOINTS.AUTH.IMPERSONATE, { user_id: userId });
    const { access, refresh, user, profile } = response.data;
    storeAuthData({ access, refresh, user, profile });
    window.location.href = '/dashboard';
  } catch (error) {
    logError('Impersonate', error);
    throw formatApiError(error);
  }
};

/**
 * Refresh user profile data from the server
 * @returns {Promise<Object>} - Updated profile data
 * @throws {Object} - Formatted error object
 */
export const refreshUserProfile = async () => {
  try {
    const response = await api.get(ENDPOINTS.AUTH.PROFILE);
    const profile = response.data;

    // ProfileSerializer returns a nested company object; normalize to the flat
    // shape that login returns so all consumers can rely on profile.company_id.
    if (profile.company && profile.company_id == null) {
      profile.company_id = profile.company.id;
      profile.company_name = profile.company.company_name;
      profile.company_profile_complete = profile.company.company_profile_complete;
    }

    // Update stored profile
    localStorage.setItem(STORAGE_KEYS.PROFILE, JSON.stringify(profile));

    // Update user data if it exists in profile
    if (profile.user) {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(profile.user));
    }

    return profile;
  } catch (error) {
    logError('Refresh profile', error);
    throw formatApiError(error);
  }
};

export const getCreditBalance = async () => {
  try {
    const response = await api.get(ENDPOINTS.AUTH.CREDIT_BALANCE);
    return response.data;
  } catch (error) {
    logError('Get credit balance', error);
    throw formatApiError(error);
  }
};
